/**
 * Prevent repeat document replacement requests in the same browser session.
 * A successful request is not retried for the same document version.
 * The server remains the source of truth for audit state and readiness.
 */
// Each mounted employee/requirement card has its own request guard. Never
// share successful document IDs across tenants or separate staff sessions:
// historical imported document IDs may collide between workspaces.
export function createReplacementRequestGuard() {
  const inFlightDocumentIds = new Set();
  const completedDocumentIds = new Set();

  return async function requestReplacementOnce(documentId, sendRequest) {
  if (!documentId || typeof sendRequest !== 'function') {
    throw new Error('Document and replacement request are required.');
  }
  if (inFlightDocumentIds.has(documentId) || completedDocumentIds.has(documentId)) {
    return false;
  }

  inFlightDocumentIds.add(documentId);
  try {
    await sendRequest();
    completedDocumentIds.add(documentId);
    return true;
  } finally {
    inFlightDocumentIds.delete(documentId);
  }
  };
}

const FALLBACK_ERROR =
  'Could not request a replacement. Refresh the document status before trying again.';

export function getReplacementErrorMessage(error) {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail.trim();
  if (Array.isArray(detail)) {
    const message = detail.map((item) => {
      if (typeof item === 'string') return item;
      if (item && typeof item.msg === 'string') return item.msg;
      return null;
    }).filter(Boolean).join('; ');
    if (message) return message;
  }
  return FALLBACK_ERROR;
}
